/* eslint-disable no-nested-ternary */
import {
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  ImageIcon,
  InfoIcon,
  Loader2Icon,
  PictureInPicture2Icon,
  PictureInPictureIcon,
  RefreshCwIcon,
  XCircleIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DOCUMENTS_EVENTS,
  DOCUMENTS_PROPERTIES,
} from '@/constants/analytics/documents-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import { useGetMatterEventsQuery } from '@/services/api/eventsService';
import {
  expandDocument,
  pinDocument,
  selectIsAtSoftLimit,
  selectIsDocPinned,
  selectPinnedDocs,
  unpinDocument,
} from '@/services/slices/pinnedDocsSlice';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { Blob } from '@/services/types/blob-types';
import { FinchEventCode } from '@/services/types/events-types';

import { SinkActionStatus } from '../SinkActionStatus';
import { ActionMenu } from './components/ActionMenu';
import { DocxPreview } from './DocxPreview';
import { EmlPreview } from './EmlPreview';
import { PreviewSheet } from './PreviewSheet';

// Format file size helper
const formatFileSize = (sizeInBytes: number): string => {
  if (!sizeInBytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = sizeInBytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

// Human-readable labels for blob-related event codes
const EVENT_LABELS: Partial<Record<FinchEventCode, string>> = {
  [FinchEventCode.BLOB_CREATED]: 'File uploaded',
  [FinchEventCode.BLOB_DELETED]: 'File deleted',
  [FinchEventCode.BLOB_TRANSFORM_COMPLETED]: 'File processed',
  [FinchEventCode.ARTIFACT_CREATED]: 'Artifact created',
  [FinchEventCode.ARTIFACT_DIGEST_CREATED]: 'Digest created',
  [FinchEventCode.ARTIFACT_DIGEST_STATUS_UPDATED]: 'Digest updated',
};

// Info popover component
const InfoPopover = ({
  file,
  matterId,
}: {
  file: Blob;
  matterId?: string;
}) => {
  const { data: eventsData, isLoading: eventsLoading } =
    useGetMatterEventsQuery(
      {
        matterId: matterId!,
        target_id: file.id,
        ordering: ['-occurred_at'],
        limit: 20,
      },
      { skip: !matterId },
    );

  const events = eventsData?.results;

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 rounded-sm transition-all hover:bg-muted/50 active:scale-95"
              aria-label="File info"
            >
              <InfoIcon className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-[10px] py-1 px-2">
          File info
        </TooltipContent>
      </Tooltip>
      <PopoverContent side="left" align="start" className="w-72 p-0">
        <div className="space-y-3 p-3">
          {/* Title section */}
          <div className="space-y-1">
            <h4
              className="text-sm font-semibold text-foreground leading-tight line-clamp-1"
              title={file.name}
            >
              {file.name}
            </h4>
            {file.original_filename && file.original_filename !== file.name && (
              <p
                className="text-xs text-muted-foreground font-mono truncate"
                title={file.original_filename}
              >
                {file.original_filename}
              </p>
            )}
          </div>

          {/* File details */}
          <div className="space-y-1.5 pt-2 border-t">
            <h5 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              Details
            </h5>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {file.file_size && (
                <>
                  <span className="text-muted-foreground">Size</span>
                  <span className="text-foreground">
                    {formatFileSize(file.file_size)}
                  </span>
                </>
              )}
              {file.file_mime_type && (
                <>
                  <span className="text-muted-foreground">Type</span>
                  <span
                    className="text-foreground truncate"
                    title={file.file_mime_type}
                  >
                    {file.file_mime_type}
                  </span>
                </>
              )}
              {file.created_at && (
                <>
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-foreground">
                    {new Date(file.created_at).toLocaleDateString()}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Activity log */}
          {matterId && (
            <div className="space-y-1.5 pt-2 border-t">
              <h5 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                Activity
              </h5>
              {eventsLoading ? (
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              ) : events && events.length > 0 ? (
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {events.map((event) => {
                    const actor = event.user
                      ? `${event.user.first_name} ${event.user.last_name}`.trim() ||
                        event.user.email
                      : event.agent ?? 'System';
                    const label =
                      EVENT_LABELS[event.event_code] ?? event.event_code;
                    return (
                      <div key={event.id} className="flex flex-col gap-0.5">
                        <span className="text-xs text-foreground font-medium">
                          {label}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {actor}
                          {event.occurred_at &&
                            ` · ${new Date(event.occurred_at).toLocaleDateString()}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground">
                  No activity recorded
                </p>
              )}
            </div>
          )}

          {/* Artifact inspector button */}
          {file.artifact_id && (
            <div className="pt-2 border-t">
              <InspectArtifactButton
                artifactId={file.artifact_id}
                artifactType={ArtifactType.BLOB}
                buttonClassName="w-full text-xs text-purple-700 hover:text-purple-900 py-1.5 px-2 rounded-md hover:bg-purple-50 transition flex items-center justify-center gap-1.5 font-medium"
                label="View Artifact Details"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface BlobPreviewProps {
  file: Blob;
  /** Matter ID for pinning functionality */
  matterId?: string;
  /** Matter name for pinning display */
  matterName?: string;
}

const PreviewMedia = ({
  downloadUrl,
  fileName,
  mimeType,
  isLoading,
  onLoad,
  onError,
}: {
  downloadUrl: string;
  fileName: string;
  mimeType: string;
  isLoading: boolean;
  onLoad: () => void;
  onError: () => void;
}) =>
  mimeType.startsWith('image/') ? (
    <img
      src={downloadUrl}
      alt={fileName}
      className={cn(
        'absolute inset-0 w-full h-full object-contain transition-opacity duration-500 z-[1]',
        isLoading ? 'opacity-0' : 'opacity-100',
      )}
      onLoad={onLoad}
      onError={onError}
    />
  ) : (
    <iframe
      src={downloadUrl}
      className={cn(
        'absolute inset-0 w-full h-full transition-opacity duration-500 z-[1]',
        isLoading ? 'opacity-0' : 'opacity-100',
      )}
      title={fileName}
      allow="fullscreen"
      onLoad={onLoad}
      onError={onError}
    />
  );

export const BlobPreview = ({
  file,
  matterId,
  matterName,
}: BlobPreviewProps) => {
  const dispatch = useDispatch();
  const capture = usePostHogCapture();
  const isPinned = useSelector(selectIsDocPinned(file.id));
  const isAtSoftLimit = useSelector(selectIsAtSoftLimit);
  const pinnedDocs = useSelector(selectPinnedDocs);

  const [state, setState] = useState({
    isLoading: true,
    error: null as string | null,
    fileId: file.id,
  });

  const { isLoading, error, fileId } = state;

  const mimeType = file.file_mime_type || 'application/octet-stream';
  const isEml = mimeType === 'message/rfc822';
  const isDocx =
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.file_extension === 'docx';
  const isPreviewable =
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.includes('text/') ||
    isEml ||
    isDocx;

  // Pin/Unpin handlers
  const handlePinToggle = () => {
    if (isPinned) {
      dispatch(unpinDocument(file.id));
      toast.success('Document unpinned');

      // Track unpin event
      capture(DOCUMENTS_EVENTS.DOCUMENT_UNPINNED, {
        [DOCUMENTS_PROPERTIES.PAGE]: 'documents',
        [DOCUMENTS_PROPERTIES.COMPONENT]: 'blob_preview',
        [DOCUMENTS_PROPERTIES.MATTER_ID]: matterId,
        [DOCUMENTS_PROPERTIES.DOCUMENT_ID]: file.id,
        [DOCUMENTS_PROPERTIES.FILE_NAME]: file.name,
        [DOCUMENTS_PROPERTIES.FILE_TYPE]:
          file.file_mime_type || 'application/octet-stream',
        [DOCUMENTS_PROPERTIES.FILE_SIZE]: file.file_size,
        [DOCUMENTS_PROPERTIES.UNPIN_SOURCE]: 'preview_button',
        [DOCUMENTS_PROPERTIES.PINNED_COUNT]: pinnedDocs.length - 1,
      });
    } else {
      if (!matterId || !matterName) {
        toast.error('Unable to pin: Matter context not available');
        return;
      }
      dispatch(
        pinDocument({
          id: file.id,
          matterId,
          matterName,
          fileName: file.name,
          fileType: file.file_mime_type || 'application/octet-stream',
          downloadUrl: file.download_url,
          pinnedAt: new Date().toISOString(),
        }),
      );
      // Auto-expand the preview panel when pinning a document
      dispatch(expandDocument(file.id));
      toast.success('Document pinned to dock');

      // Track pin event
      capture(DOCUMENTS_EVENTS.DOCUMENT_PINNED, {
        [DOCUMENTS_PROPERTIES.PAGE]: 'documents',
        [DOCUMENTS_PROPERTIES.COMPONENT]: 'blob_preview',
        [DOCUMENTS_PROPERTIES.MATTER_ID]: matterId,
        [DOCUMENTS_PROPERTIES.DOCUMENT_ID]: file.id,
        [DOCUMENTS_PROPERTIES.FILE_NAME]: file.name,
        [DOCUMENTS_PROPERTIES.FILE_TYPE]:
          file.file_mime_type || 'application/octet-stream',
        [DOCUMENTS_PROPERTIES.FILE_SIZE]: file.file_size,
        [DOCUMENTS_PROPERTIES.PINNED_COUNT]: pinnedDocs.length + 1,
      });

      if (isAtSoftLimit) {
        toast.info('Consider unpinning some documents', {
          description: 'You have many documents pinned',
        });
      }
    }
  };

  const canPin = !!matterId && !!matterName;

  // Stabilize the download URL so that polling-induced presigned URL changes
  // don't cause the iframe/image to reload. Only update when the file changes.
  const stableDownloadUrl = useRef(file.download_url);
  if (file.id !== state.fileId) {
    stableDownloadUrl.current = file.download_url;
  }

  // Reset loading state when file changes
  useEffect(() => {
    if (file.id !== fileId) {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        fileId: file.id,
      }));
    }
  }, [file.id, fileId]);

  const updateState = (newState: Partial<typeof state>) =>
    setState((prev) => ({ ...prev, ...newState }));

  const handlePreviewLoad = () => updateState({ isLoading: false });
  const handlePreviewError = () =>
    updateState({ isLoading: false, error: 'Failed to load preview' });

  const handleRetry = () => {
    updateState({ isLoading: true, error: null });
    setTimeout(() => {
      const iframe = document.querySelector('iframe');
      if (iframe) {
        // Re-assign the iframe's own src to force a reload
        // eslint-disable-next-line no-self-assign
        iframe.src = iframe.src;
      }
    }, 100);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-xs font-medium text-foreground">Preview</h3>
          {matterId && file.sink_actions && file.sink_actions.length > 0 && (
            <>
              <span className="text-muted-foreground">•</span>
              <SinkActionStatus
                sinkAction={file.sink_actions[0]}
                matterId={matterId}
                enableContinuousPolling
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <InfoPopover file={file} matterId={matterId} />
          <PreviewSheet file={file} />
          <ActionMenu
            downloadUrl={file.download_url}
            fileName={file.name}
            fileExtension={file.file_extension}
          />
        </div>
      </div>

      {!isPreviewable ? (
        <div className="flex flex-col items-center justify-center border border-dashed rounded-md bg-muted/30 p-6 min-h-[300px] gap-4">
          <div className="bg-background rounded-full p-4 shadow-sm">
            <FileIcon className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="flex flex-col items-center text-center">
            <h4 className="font-medium text-sm mb-1">{file.name}</h4>
            <p className="text-xs text-muted-foreground mb-4">
              This file type cannot be previewed
            </p>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                const a = document.createElement('a');
                a.href = file.download_url;
                a.download = file.name;
                a.click();
              }}
              className="h-8 text-xs px-3 active:scale-95 transition-transform"
            >
              <DownloadIcon className="h-3.5 w-3.5 mr-1.5" />
              Download
            </Button>
          </div>
        </div>
      ) : isEml ? (
        <div className="flex-1 min-h-[300px]">
          <EmlPreview file={file} />
        </div>
      ) : isDocx ? (
        <div className="relative flex-1 rounded-md border bg-muted/30 min-h-[300px]">
          <DocxPreview
            file={file}
            onLoad={handlePreviewLoad}
            isLoading={isLoading}
            matterId={matterId}
          />

          {canPin && !isLoading && !error && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handlePinToggle}
                    className={cn(
                      'h-8 px-4 rounded-full text-xs font-medium shadow-lg transition-all active:scale-95',
                      isPinned
                        ? 'bg-zinc-700 hover:bg-zinc-800 text-white'
                        : 'bg-primary hover:bg-primary/90 text-white',
                    )}
                  >
                    {isPinned ? (
                      <>
                        <PictureInPictureIcon className="h-3.5 w-3.5 mr-1.5" />
                        Put it Back
                      </>
                    ) : (
                      <>
                        <PictureInPicture2Icon className="h-3.5 w-3.5 mr-1.5" />
                        Keep on Screen
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="text-xs max-w-[220px] text-center"
                >
                  {isPinned
                    ? 'Click to remove from floating dock'
                    : 'Floats this document so it stays with you across the app to save time.'}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      ) : (
        <div className="relative flex-1 rounded-md border bg-muted/30">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[1px] z-10">
              <div className="flex flex-col items-center gap-2 bg-background/70 p-4 rounded-lg">
                <Loader2Icon className="h-5 w-5 animate-spin text-primary" />
                <span className="text-xs text-muted-foreground font-medium">
                  Loading preview...
                </span>
                <Skeleton className="h-2 w-24 rounded-full" />
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/90 z-10">
              <div className="flex flex-col items-center gap-3 max-w-[85%] text-center">
                <XCircleIcon className="h-8 w-8 text-destructive/70" />
                <span className="text-sm text-destructive font-medium">
                  {error}
                </span>
                <p className="text-xs text-muted-foreground mb-1">
                  The file preview could not be loaded
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className="h-8 text-xs px-3 active:scale-95 transition-transform"
                  >
                    <RefreshCwIcon className="h-3.5 w-3.5 mr-1.5" />
                    Retry
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => window.open(file.download_url, '_blank')}
                    className="h-8 text-xs px-3 active:scale-95 transition-transform"
                  >
                    <ExternalLinkIcon className="h-3.5 w-3.5 mr-1.5" />
                    Open in New Tab
                  </Button>
                </div>
              </div>
            </div>
          )}

          <PreviewMedia
            downloadUrl={stableDownloadUrl.current}
            fileName={file.name}
            mimeType={mimeType}
            isLoading={isLoading}
            onLoad={handlePreviewLoad}
            onError={handlePreviewError}
          />

          {/* Floating Keep on Screen Button - Bottom Center */}
          {canPin && !isLoading && !error && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handlePinToggle}
                    className={cn(
                      'h-8 px-4 rounded-full text-xs font-medium shadow-lg transition-all active:scale-95',
                      isPinned
                        ? 'bg-zinc-700 hover:bg-zinc-800 text-white'
                        : 'bg-primary hover:bg-primary/90 text-white',
                    )}
                  >
                    {isPinned ? (
                      <>
                        <PictureInPictureIcon className="h-3.5 w-3.5 mr-1.5" />
                        Put it Back
                      </>
                    ) : (
                      <>
                        <PictureInPicture2Icon className="h-3.5 w-3.5 mr-1.5" />
                        Keep on Screen
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="text-xs max-w-[220px] text-center"
                >
                  {isPinned
                    ? 'Click to remove from floating dock'
                    : 'Floats this document so it stays with you across the app to save time.'}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlobPreview;
